import moment from 'moment-timezone'
import { useState, useEffect } from 'react'
import ms from 'ms'
import usePrevious from '../usePrevious'
import { areTimesEqual, calcTimeRemaining } from './utils'

const FLASHING_THRESHOLD_SECONDS = ms('2 minutes') / 1000
const MAX_ELAPSED_TIME_SKEW_ALLOWED_MILLISECONDS = ms('2 minutes') // a bit arbitrary

/**
 * Countdown hook that returns countdown data
 * @param  {moment object} currentServerTime (optional)
 * @param  {moment object} endTime The time to count down to
 * @param  {number} updateFrequency How often to update the countdown data (ms)
 * @return {object} Object that provides the number of seconds remaining and other data
 *
 * Using currentServerTime ensures that the server-side render and the first
 * client-side render (which might occur a few seconds later) use the same time.
 * Also, with each poll if an updated currentServerTime is passed we use that.
 * In between polls, we rely on the localElapsedTime (using client clock)
 *
 * The startTime is basically the currentServerTime in state.  This was needed for the
 * case where caller does not pass currentServerTime, and we only want the initial
 * moment (or else we run into a potential re-render loop).
 *
 * Picture of how this works... (time increasing left to right)
 *
 * INITIALLY
 *
 *  currentServerTime                                             endTime
 *  localTimeStamp
 *  ↓                                                                   ↓
 *  [<---------------------- baseTimeRemaining ------------------------>]
 *  [] ← localElapsedTime
 *  [<----------------------   timeRemaining   ------------------------>]
 *
 * LATER... (after localElapsedTime has elapsed)
 *
 *  currentServerTime                                             endTime
 *  localTimeStamp
 *  ↓                                                                   ↓
 *  [<---------------------- baseTimeRemaining ------------------------>]
 *  [<------>]] ← localElapsedTime
 *            [<------------------   timeRemaining   ------------------>]
 *
 * AFTER a "reset"
 *
 *  currentServerTime                                             endTime
 *                       localTimeStamp
 *  ↓                    ↓                                              ↓
 *                       [<------------ baseTimeRemaining ------------->]
 *                       [] ← localElapsedTime
 *                       [<------------   timeRemaining   ------------->]
 *
 * AFTER currentServerTime update
 *
 *                                   currentServerTime            endTime
 *                                   localTimeStamp
 *                                   ↓                                  ↓
 *                                   [<------ baseTimeRemaining ------->]
 *                                   [] ← localElapsedTime
 *                                   [<-------   timeRemaining   ------>]
 */
export default ({
  currentServerTime = null,
  endTime, // required, TODO: figure out how to handle endTime not passed.
  updateFrequency = 1000,
}) => {
  const [startTime, setStartTime] = useState(currentServerTime)
  const previousStartTime = usePrevious(startTime, startTime)
  const previousEndTime = usePrevious(endTime, endTime)
  const [localTimestamp, setLocalTimestamp] = useState(moment())
  const [localElapsedTime, setLocalElapsedTime] = useState(0)

  const initialTime = calcTimeRemaining(startTime, endTime) // tracked in ms

  /**
   * baseTimeRemaining reflects the time remaining without localElapsedTime factored in,
   * i.e. our best known time remaining when we init (here) or get a new server time.
   */
  const [baseTimeRemaining, setBaseTimeRemaining] = useState(initialTime)

  /**
   * timeRemaining is the actual time returned from the hook (for consumers to use).
   */
  const [timeRemaining, setTimeRemaining] = useState(initialTime)

  /**
   * startTime tracks currentServerTime normally, however in the case where the caller
   * doesn't pass currentServerTime, we must capture it based on local machine time.
   * We only capture the latter once, or else we end up in a render loop with
   * a new time getting created each time (potentially).
   */
  useEffect(() => {
    if (!currentServerTime) {
      setStartTime(moment()) // for when caller doesn't bother to tell us the time
    } else {
      setStartTime(currentServerTime) // for server time updates (e.g. via polling)
    }
  }, [currentServerTime])

  /**
   * Updates based on changes to current server time or end time
   */
  useEffect(() => {
    if (
      !areTimesEqual(previousStartTime, startTime) ||
      !areTimesEqual(previousEndTime, endTime)
    ) {
      const time = calcTimeRemaining(currentServerTime, endTime)
      setTimeRemaining(time)
      setBaseTimeRemaining(time)
      setLocalTimestamp(moment())
      setLocalElapsedTime(0)
    }
  }, [startTime, previousStartTime, endTime, previousEndTime])

  /**
   * Check to see if we skew a lot, as it could be user changed their local clock.
   * Regardless, we take this opportunity to "reset" and start tracking based on new
   * local time.
   */
  useEffect(() => {
    if (
      Math.abs(localElapsedTime) > MAX_ELAPSED_TIME_SKEW_ALLOWED_MILLISECONDS
    ) {
      // have to subtract updateFrequency as timeRemaining is from prior interval
      setBaseTimeRemaining(
        Math.max(timeRemaining - Math.max(0, updateFrequency), -1000)
      )
      setLocalTimestamp(moment())
      setLocalElapsedTime(0)
    }
  }, [localElapsedTime, timeRemaining, updateFrequency])

  /**
   * Interval timer for tracking localElapsedTime.
   */
  useEffect(() => {
    const updateElapsedTimeCallback = () => {
      setLocalElapsedTime(moment().diff(localTimestamp, 'milliseconds'))
    }
    updateElapsedTimeCallback()
    const intervalId = setInterval(
      updateElapsedTimeCallback,
      updateFrequency - 0.5 // .5 ms fudge factor to avoid exact time boundary matches.
    )
    return () => clearInterval(intervalId)
  }, [updateFrequency, localTimestamp])

  /**
   * This sets the exposed time remaining that is returned to the caller
   */
  useEffect(() => {
    setTimeRemaining(
      Math.max(baseTimeRemaining - Math.max(0, localElapsedTime), -1000)
    )
  }, [localElapsedTime, baseTimeRemaining])

  /**
   * Logically one would think time has elapsed when we === 0, however for backward
   * compatibility we don't flag it as elapsed until it goes past 0 to negative territory
   */
  const isElapsed = timeRemaining < 0
  const shouldFlash =
    Math.floor(timeRemaining / 1000) <= FLASHING_THRESHOLD_SECONDS

  return {
    secondsRemaining: Math.floor(timeRemaining / 1000),
    isElapsed,
    shouldFlash,
  }
}
